/**
 * Minimal DICOM Part-10 parser + pixel-data extractor.
 *
 * Scope for Sprint 2:
 *   * 128-byte preamble + DICM magic
 *   * File Meta Information (group 0002, always Explicit VR Little Endian)
 *   * Main dataset in Explicit VR Little Endian (TS = 1.2.840.10008.1.2.1)
 *   * Native pixel data — MONOCHROME2 (8-bit + 16-bit) and RGB 8-bit
 *
 * Compressed TS (JPEG, JPEG-LS, RLE, J2K) and Implicit / Big Endian
 * land in Sprint 3 through the Zig-WASM decoder. This pure-TS path
 * covers every modality smoke row today.
 */

export type Vr = string;   // two-letter VR like "UI", "CS", "PN", "US", "OB", "OW", "SQ", etc.
export type Tag = number;  // (group << 16) | element

export interface DicomElement {
  tag: Tag;
  vr: Vr;
  length: number;
  value: ArrayBuffer;
}

export interface ParsedDicom {
  meta: Map<Tag, DicomElement>;
  dataset: Map<Tag, DicomElement>;
  transferSyntaxUid: string;
}

const MAGIC = [0x44, 0x49, 0x43, 0x4d]; // "DICM"

/** Compose a tag from group + element. */
export function T(g: number, e: number): Tag {
  return (g << 16) | e;
}

/** Explicit VR Little Endian parser (TS 1.2.840.10008.1.2.1). */
export function parseDicom(buf: ArrayBuffer): ParsedDicom {
  const view = new DataView(buf);
  // 128-byte preamble + DICM magic
  for (let i = 0; i < 4; i++) {
    if (view.getUint8(128 + i) !== MAGIC[i]) {
      throw new Error("Not a Part-10 DICOM file (DICM magic missing)");
    }
  }
  let offset = 132;

  // --- File Meta (group 0002) — always Explicit VR LE ---
  const meta = new Map<Tag, DicomElement>();
  while (offset < buf.byteLength) {
    const g = view.getUint16(offset, true);
    if (g !== 0x0002) break;
    const { element, next } = readExplicitVrElement(view, offset, buf);
    meta.set(element.tag, element);
    offset = next;
  }
  const tsUid = meta.has(T(0x0002, 0x0010))
    ? decodeString(meta.get(T(0x0002, 0x0010))!.value).replace(/\0+$/, "")
    : "1.2.840.10008.1.2";

  const EXPLICIT_LE = "1.2.840.10008.1.2.1";
  const IMPLICIT_LE = "1.2.840.10008.1.2";
  if (tsUid !== EXPLICIT_LE && tsUid !== IMPLICIT_LE) {
    // Compressed transfer syntaxes (JPEG, JPEG-2000, RLE) still need
    // a codec pass. Those are decompressed server-side during ingest
    // where possible; this error fires when we hit a SOP the SCP
    // didn't transcode.
    throw new Error(
      `Unsupported transfer syntax ${tsUid} — decoder handles 1.2.840.10008.1.2 (Implicit VR LE) and 1.2.840.10008.1.2.1 (Explicit VR LE). Compressed paths require an upstream transcode.`,
    );
  }

  // --- Main dataset ---
  const implicit = tsUid === IMPLICIT_LE;
  const dataset = new Map<Tag, DicomElement>();
  while (offset < buf.byteLength) {
    const { element, next } = implicit
      ? readImplicitVrElement(view, offset, buf)
      : readExplicitVrElement(view, offset, buf);
    dataset.set(element.tag, element);
    offset = next;
  }

  return { meta, dataset, transferSyntaxUid: tsUid };
}

function readExplicitVrElement(
  view: DataView,
  at: number,
  buf: ArrayBuffer,
): { element: DicomElement; next: number } {
  const g = view.getUint16(at, true);
  const e = view.getUint16(at + 2, true);
  const tag = (g << 16) | e;
  const vr = String.fromCharCode(view.getUint8(at + 4), view.getUint8(at + 5));
  let length: number;
  let valueOffset: number;

  // VRs with 32-bit length: OB OW OF SQ UT UN
  if (vr === "OB" || vr === "OW" || vr === "OF" || vr === "SQ" || vr === "UT" || vr === "UN") {
    // 2 reserved bytes, then 32-bit length
    length = view.getUint32(at + 8, true);
    valueOffset = at + 12;
  } else {
    length = view.getUint16(at + 6, true);
    valueOffset = at + 8;
  }

  const value = (length === 0xffffffff)
    ? new ArrayBuffer(0) // undefined length — Sprint 3 will handle
    : buf.slice(valueOffset, valueOffset + length);
  const next = valueOffset + (length === 0xffffffff ? 0 : length);
  return { element: { tag, vr, length, value }, next };
}

/**
 * Implicit VR Little Endian element layout:
 *   group(2) · element(2) · length(4)  [no VR, no reserved bytes]
 *
 * The VR is implied by the data-dictionary lookup on the tag. We
 * don't ship the full dictionary client-side; downstream readers
 * interpret the raw value buffer themselves, so leaving VR as "UN"
 * is sufficient. `decodePixels` treats PixelData as raw bytes.
 *
 * Undefined-length handling:
 *   - For a Sequence (or anything else with length = 0xFFFFFFFF),
 *     scan forward for the SequenceDelimitationItem (FFFE,E0DD)
 *     marker. We don't attempt to parse the sequence's items; the
 *     viewer only needs top-level attributes. Without this scan the
 *     outer parser loop would spin forever because the naive "advance
 *     by length" step becomes a no-op.
 */
function readImplicitVrElement(
  view: DataView,
  at: number,
  buf: ArrayBuffer,
): { element: DicomElement; next: number } {
  const g = view.getUint16(at, true);
  const e = view.getUint16(at + 2, true);
  const tag = (g << 16) | e;
  const length = view.getUint32(at + 4, true);
  const valueOffset = at + 8;
  if (length === 0xffffffff) {
    const end = scanSequenceDelimiter(view, valueOffset, buf.byteLength);
    return {
      element: { tag, vr: "UN", length, value: buf.slice(valueOffset, end) },
      next: end,
    };
  }
  const value = buf.slice(valueOffset, valueOffset + length);
  return {
    element: { tag, vr: "UN", length, value },
    next: valueOffset + length,
  };
}

/** Scan forward from `start` looking for the SequenceDelimitationItem
 *  (FFFE,E0DD) marker. We don't try to parse inside the sequence —
 *  we just want the offset past the delimiter so the outer parser
 *  can resume at the next top-level element. Aligned to 2-byte
 *  boundaries, which is the DICOM alignment guarantee. */
function scanSequenceDelimiter(view: DataView, start: number, max: number): number {
  for (let at = start; at + 8 <= max; at += 2) {
    const g = view.getUint16(at, true);
    if (g !== 0xfffe) continue;
    const e = view.getUint16(at + 2, true);
    if (e === 0xe0dd) return at + 8;
  }
  return max;
}

function decodeString(ab: ArrayBuffer): string {
  return new TextDecoder("ascii").decode(new Uint8Array(ab));
}

export function getString(ds: Map<Tag, DicomElement>, tag: Tag): string | undefined {
  const el = ds.get(tag);
  if (!el) return undefined;
  return new TextDecoder("iso-8859-1").decode(new Uint8Array(el.value)).replace(/\0+$/, "").trim();
}

export function getUint16(ds: Map<Tag, DicomElement>, tag: Tag): number | undefined {
  const el = ds.get(tag);
  if (!el || el.value.byteLength < 2) return undefined;
  return new DataView(el.value).getUint16(0, true);
}

export function getInt32(ds: Map<Tag, DicomElement>, tag: Tag): number | undefined {
  const el = ds.get(tag);
  if (!el || el.value.byteLength < 4) return undefined;
  return new DataView(el.value).getInt32(0, true);
}

export function getFloat(ds: Map<Tag, DicomElement>, tag: Tag): number | undefined {
  const el = ds.get(tag);
  if (!el) return undefined;
  // Stored as a DS string in ASCII.
  const s = new TextDecoder("ascii").decode(new Uint8Array(el.value)).trim();
  const first = s.split("\\")[0];
  const n = parseFloat(first);
  return Number.isFinite(n) ? n : undefined;
}

/** Read a multi-valued DS (decimal string) tag and return its float
 *  values in order. Used for e.g. PixelSpacing (row\col) and
 *  ImagePositionPatient (x\y\z). */
export function getFloats(ds: Map<Tag, DicomElement>, tag: Tag): number[] | undefined {
  const el = ds.get(tag);
  if (!el) return undefined;
  const s = new TextDecoder("ascii").decode(new Uint8Array(el.value)).trim();
  if (!s) return undefined;
  const parts = s.split("\\").map((p) => parseFloat(p));
  return parts.every((n) => Number.isFinite(n)) ? parts : undefined;
}

// ----------------------------------------------------------------------
//  Pixel-data decode (native, non-encapsulated)
// ----------------------------------------------------------------------

export interface DecodedPixels {
  /** Canvas-drawable RGBA buffer (pre-windowed). */
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  /** For monochrome images, the raw HU / stored-pixel array so UI can re-window. */
  stored?: Int16Array | Uint16Array | Uint8Array;
  minStored: number;
  maxStored: number;
  photometric: "MONOCHROME1" | "MONOCHROME2" | "RGB";
  bitsStored: number;
  rescaleSlope: number;
  rescaleIntercept: number;
  /** Row / column spacing in mm, from PixelSpacing (0028,0030) — or
   *  ImagerPixelSpacing (0018,1164) as a fallback for projection
   *  radiography. Undefined when the SOP ships neither, in which case
   *  measurement labels fall back to px. */
  pixelSpacing?: [number, number];
  /** ImagePositionPatient (0020,0032) — position in mm of the top-left
   *  voxel in the patient coordinate system. Needed for 3D volume
   *  assembly (slice ordering + slab-to-world registration). */
  imagePositionPatient?: [number, number, number];
  /** ImageOrientationPatient (0020,0037) — row direction cosine (x,y,z)
   *  concatenated with column direction cosine (x,y,z) in the patient
   *  coordinate system. The slice normal is row × col. */
  imageOrientationPatient?: [number, number, number, number, number, number];
  /** SliceThickness (0018,0050) in mm — fallback Z spacing when only
   *  one slice is decoded. For volumes we always compute the true
   *  spacing from consecutive ImagePositionPatient differences. */
  sliceThickness?: number;
}

export function decodePixels(parsed: ParsedDicom, opts: { windowCenter?: number; windowWidth?: number } = {}): DecodedPixels {
  const ds = parsed.dataset;
  const rows = getUint16(ds, T(0x0028, 0x0010));
  const cols = getUint16(ds, T(0x0028, 0x0011));
  const bitsAllocated = getUint16(ds, T(0x0028, 0x0100)) ?? 8;
  const bitsStored    = getUint16(ds, T(0x0028, 0x0101)) ?? bitsAllocated;
  const pixelRep      = getUint16(ds, T(0x0028, 0x0103)) ?? 0;
  const photoStr      = getString(ds, T(0x0028, 0x0004)) ?? "MONOCHROME2";
  const samplesPerPx  = getUint16(ds, T(0x0028, 0x0002)) ?? 1;
  const rescaleSlope     = getFloat(ds, T(0x0028, 0x1053)) ?? 1.0;
  const rescaleIntercept = getFloat(ds, T(0x0028, 0x1052)) ?? 0.0;
  const dsWc = getFloat(ds, T(0x0028, 0x1050));
  const dsWw = getFloat(ds, T(0x0028, 0x1051));

  // PixelSpacing (0028,0030) is DS pair "row\col" in mm. For projection
  // radiography (CR/DX/MG) DICOM emits ImagerPixelSpacing (0018,1164)
  // instead — identical semantics. Keep both paths so measurements work
  // on every modality the SCP serves.
  const rawSpacing = getFloats(ds, T(0x0028, 0x0030))
                 ?? getFloats(ds, T(0x0018, 0x1164));
  let pixelSpacing: [number, number] | undefined;
  if (rawSpacing && rawSpacing.length >= 2
      && rawSpacing[0] > 0 && rawSpacing[1] > 0) {
    pixelSpacing = [rawSpacing[0], rawSpacing[1]];
  }

  // 3D spatial metadata — used by the volume renderer to order slices
  // along the acquisition normal and to register voxels to the patient
  // coordinate system. See PS3.3 C.7.6.2.1.1 — ImagePositionPatient is
  // the position in mm of the centre of the (0,0) voxel;
  // ImageOrientationPatient is [rowX, rowY, rowZ, colX, colY, colZ].
  const ipp = getFloats(ds, T(0x0020, 0x0032));
  const iop = getFloats(ds, T(0x0020, 0x0037));
  const imagePositionPatient: [number, number, number] | undefined =
    ipp && ipp.length >= 3 ? [ipp[0], ipp[1], ipp[2]] : undefined;
  const imageOrientationPatient:
    [number, number, number, number, number, number] | undefined =
    iop && iop.length >= 6
      ? [iop[0], iop[1], iop[2], iop[3], iop[4], iop[5]]
      : undefined;
  const sliceThickness = getFloat(ds, T(0x0018, 0x0050));

  if (!rows || !cols) throw new Error("PixelData has no Rows/Columns");
  const pixEl = ds.get(T(0x7FE0, 0x0010));
  if (!pixEl) throw new Error("PixelData (7FE0,0010) missing");

  const photometric = (photoStr === "MONOCHROME1" || photoStr === "MONOCHROME2" || photoStr === "RGB") ? photoStr : "MONOCHROME2";

  // WSI tile-matrix metadata. Only present on multi-frame SM SOPs.
  // NumberOfFrames (0028,0008) is IS — an ASCII decimal string in
  // both transport syntaxes, not a binary integer. Parse it as such,
  // with a binary fallback for the rare SOP that encodes it differently.
  const nFramesStr = getString(ds, T(0x0028, 0x0008));
  const nFramesFromStr = nFramesStr ? parseInt(nFramesStr, 10) : NaN;
  const nFrames = Number.isFinite(nFramesFromStr) && nFramesFromStr > 0
    ? nFramesFromStr
    : (getInt32(ds, T(0x0028, 0x0008)) ?? getUint16(ds, T(0x0028, 0x0008)) ?? 1);
  // TotalPixelMatrixColumns / Rows are UL — 4 bytes binary in every
  // transport syntax — so the binary read is correct.
  const totalCols = getInt32(ds, T(0x0048, 0x0006));
  const totalRows = getInt32(ds, T(0x0048, 0x0007));

  if (photometric === "RGB" && samplesPerPx === 3) {
    const src = new Uint8Array(pixEl.value);
    // ------------------------------------------------------------------
    // Multi-frame tiled WSI — assemble the N tiles into one big RGBA
    // canvas using the TotalPixelMatrix columns / rows to compute the
    // tile grid. Assumes TILED_FULL organisation (the only one IDC's
    // conversion emits in practice).
    // ------------------------------------------------------------------
    // Sanity bounds — a corrupt tag can produce absurd dimensions.
    // Max output canvas = 8192 × 8192 (67 Mpx); bigger volumes need a
    // proper pyramid-aware viewer, not this single-level assembler.
    const MAX_ASSEMBLED_DIM = 8192;
    if (nFrames > 1 && totalCols && totalRows && totalCols > 0 && totalRows > 0
        && totalCols <= MAX_ASSEMBLED_DIM && totalRows <= MAX_ASSEMBLED_DIM) {
      const tilesPerRow = Math.ceil(totalCols / cols);
      const tilesPerCol = Math.ceil(totalRows / rows);
      const W = tilesPerRow * cols;
      const H = tilesPerCol * rows;
      // Also clamp iteration count — never try to read more frames than
      // the pixel buffer actually holds.
      const perFrameBytes = rows * cols * 3;
      const maxFramesInBuffer = Math.floor(pixEl.value.byteLength / perFrameBytes);
      const nFramesClamped = Math.min(nFrames, maxFramesInBuffer,
                                      tilesPerRow * tilesPerCol);
      const rgba = new Uint8ClampedArray(W * H * 4);
      const perFrame = perFrameBytes;
      for (let f = 0; f < nFramesClamped; f++) {
        const tx = (f % tilesPerRow) * cols;
        const ty = Math.floor(f / tilesPerRow) * rows;
        const src0 = f * perFrame;
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const s = src0 + (y * cols + x) * 3;
            const d = ((ty + y) * W + (tx + x)) * 4;
            rgba[d]     = src[s];
            rgba[d + 1] = src[s + 1];
            rgba[d + 2] = src[s + 2];
            rgba[d + 3] = 0xff;
          }
        }
      }
      return {
        rgba, width: W, height: H, stored: undefined,
        minStored: 0, maxStored: 255,
        photometric, bitsStored, rescaleSlope, rescaleIntercept,
        pixelSpacing, imagePositionPatient, imageOrientationPatient,
        sliceThickness,
      };
    }
    // Single-frame (overview / label / thumbnail) — straight RGB → RGBA.
    const totalPx = rows * cols;
    const rgba = new Uint8ClampedArray(totalPx * 4);
    for (let i = 0, j = 0; i < totalPx; i++, j += 4) {
      rgba[j + 0] = src[i * 3 + 0];
      rgba[j + 1] = src[i * 3 + 1];
      rgba[j + 2] = src[i * 3 + 2];
      rgba[j + 3] = 0xff;
    }
    return {
      rgba, width: cols, height: rows, stored: undefined,
      minStored: 0, maxStored: 255,
      photometric, bitsStored, rescaleSlope, rescaleIntercept,
      pixelSpacing, imagePositionPatient, imageOrientationPatient,
      sliceThickness,
    };
  }

  // legacy single-frame path for the MONO branches below — keeps their
  // buffer allocation unchanged.
  const totalPx = rows * cols;
  const rgba = new Uint8ClampedArray(totalPx * 4);

  // MONOCHROME — apply rescale + window/level → 8-bit.
  let stored: Int16Array | Uint16Array | Uint8Array;
  if (bitsAllocated <= 8) {
    stored = new Uint8Array(pixEl.value);
  } else if (pixelRep === 0) {
    stored = new Uint16Array(pixEl.value);
  } else {
    stored = new Int16Array(pixEl.value);
  }

  // min / max of stored values (to set a sensible default W/L if none)
  let minS = Infinity, maxS = -Infinity;
  for (let i = 0; i < stored.length; i++) {
    const v = stored[i];
    if (v < minS) minS = v;
    if (v > maxS) maxS = v;
  }
  const minHu = minS * rescaleSlope + rescaleIntercept;
  const maxHu = maxS * rescaleSlope + rescaleIntercept;
  const wc = opts.windowCenter ?? dsWc ?? (minHu + maxHu) / 2;
  const ww = opts.windowWidth  ?? dsWw ?? Math.max(1, maxHu - minHu);
  const lo = wc - ww / 2;
  const hi = wc + ww / 2;
  const invert = photometric === "MONOCHROME1";

  for (let i = 0, j = 0; i < stored.length; i++, j += 4) {
    const hu = stored[i] * rescaleSlope + rescaleIntercept;
    let t = (hu - lo) / (hi - lo);
    if (t < 0) t = 0; else if (t > 1) t = 1;
    let px = Math.round(t * 255);
    if (invert) px = 255 - px;
    rgba[j + 0] = px;
    rgba[j + 1] = px;
    rgba[j + 2] = px;
    rgba[j + 3] = 0xff;
  }

  return {
    rgba, width: cols, height: rows, stored,
    minStored: minS, maxStored: maxS,
    photometric, bitsStored, rescaleSlope, rescaleIntercept,
    pixelSpacing, imagePositionPatient, imageOrientationPatient,
    sliceThickness,
  };
}
